import {type Camera3DControl} from '../MediaCard';

/**
 * Shared scene model for point-cloud viewers. Parse, truncation, and box
 * filtering stay outside the viewer and hand it a scene.
 */
export type PointCloudPosition = [x: number, y: number, z: number];
export type PointCloudRgb = [r: number, g: number, b: number];
export type PointCloudRgba = {r: number; g: number; b: number; a: number};

export type PointCloudPoint = {
  position: PointCloudPosition;
  color?: PointCloudRgb;
  category?: number;
};

export type PointCloudVector = {
  start: PointCloudPosition;
  end: PointCloudPosition;
  color?: PointCloudRgb;
};

export type PointCloudBox = {
  edges: Array<[PointCloudPosition, PointCloudPosition]>;
  label?: string;
  color: PointCloudRgb;
  score?: number;
};

export type PointCloudScene = {
  points: PointCloudPoint[];
  vectors: PointCloudVector[];
  boxes: PointCloudBox[];
};

export const POINT_CLOUD_RENDERERS = ['babylon', 'potree'] as const;
export type PointCloudRendererId = (typeof POINT_CLOUD_RENDERERS)[number];

export const POINT_CLOUD_PRESENTATIONS = ['card', 'fullscreen'] as const;
export type PointCloudPresentation = (typeof POINT_CLOUD_PRESENTATIONS)[number];

/** Bands required on `pointCloud.*` timings. 1M is the band edge, not K_fs. */
export const POINT_CLOUD_POINT_COUNT_BANDS = [
  'le300k',
  '300k_1M',
  'gt1M',
] as const;
export type PointCloudPointCountBand =
  (typeof POINT_CLOUD_POINT_COUNT_BANDS)[number];

export const POINT_CLOUD_CARD_POINT_LIMIT = 300_000;
export const POINT_CLOUD_BAND_POINT_LIMIT = 1_000_000;

export type PointCloudTimingVital =
  | 'pointCloud.parse'
  | 'pointCloud.snapshotRender'
  | 'pointCloud.fullscreenReady';

export type PointCloudViewerTimingVital =
  | 'pointCloud.snapshotRender'
  | 'pointCloud.fullscreenReady';

export type PointCloudTiming = {
  vital: PointCloudTimingVital;
  durationMs: number;
  renderer: PointCloudRendererId;
  pointCountBand: PointCloudPointCountBand;
  presentation?: PointCloudPresentation;
};

export type PointCloudViewerTiming = PointCloudTiming & {
  vital: PointCloudViewerTimingVital;
  presentation: PointCloudPresentation;
};

export type PointCloudTimingHook = (timing: PointCloudTiming) => void;

export type PointCloudCardResult = {
  imageUrl: string;
  cleanup?: () => void;
};

export type PointCloudFullscreenSession = {
  cleanup?: () => void;
};

/**
 * Library-specific draw path. Babylon and Potree each provide one.
 * They draw `scene.boxes` and `scene.vectors`; they do not emit product
 * analytics or media-tile vitals.
 */
export type PointCloudRenderer = {
  readonly id: PointCloudRendererId;
  renderCard(
    request: PointCloudCardRenderRequest
  ): Promise<PointCloudCardResult>;
  mountFullscreen(
    request: PointCloudFullscreenRenderRequest
  ): Promise<PointCloudFullscreenSession>;
};

export type PointCloudCardRenderRequest = {
  scene: PointCloudScene;
  width: number;
  height: number;
  backgroundColor?: PointCloudRgba;
  pointColor?: PointCloudRgb;
  useRightHandedSystem?: boolean;
  cameraControl?: Camera3DControl;
};

export type PointCloudFullscreenRenderRequest = {
  scene: PointCloudScene;
  domElement: HTMLElement;
  backgroundColor?: PointCloudRgba;
  pointColor?: PointCloudRgb;
  useRightHandedSystem?: boolean;
};

/**
 * Shared React surface both library renderers plug into.
 *
 * Existing workspace metrics stay the caller's responsibility (or are fired
 * here as the shared shell):
 * - `trackSuccess` / `trackError` → `mediaBrowserPanelTile.total` and
 *   `mediaBrowserPanelTileTracking`
 * - `pointCloudViewed` → `3D Point Cloud Viewed` (`preview` / `fullscreen`)
 *
 * New `pointCloud.*` timings are additive. `pointCloud.parse` belongs to
 * shared preprocess. This component reports `pointCloud.snapshotRender` and
 * `pointCloud.fullscreenReady` around the renderer calls.
 */
export type PointCloudViewerProps = {
  scene: PointCloudScene;
  width: number;
  height: number;
  renderer: PointCloudRenderer;
  /** Parsed file length before presentation sampling. Not `scene.points.length`. */
  sourcePointCount: number;
  backgroundColor?: PointCloudRgba;
  /** When set, every point is drawn in this color instead of its logged color or category. */
  pointColor?: PointCloudRgb;
  /** Omit or false keeps the app left-handed default. */
  useRightHandedSystem?: boolean;
  cameraControl?: Camera3DControl;
  onTiming?: PointCloudTimingHook;
  trackSuccess?: () => void;
  trackError?: () => void;
};
